/**
 * Transfer Action Creators
 */
import { Kushki } from "@kushki/js";
import { BankListResponse } from "@kushki/js/lib/types/bank_list_response";
import { ErrorResponse } from "@kushki/js/lib/types/error_response";
import { SecureOtpRequest } from "@kushki/js/lib/types/secure_otp_request";
import { SecureOtpResponse } from "@kushki/js/lib/types/secure_otp_response";
import { setErrorMessage } from "components/Error/store/Actions";
import {
  hideOverlay,
  showOverlay,
} from "components/OverLay/store/ActionCreators";
import { IConfrontaAnswer } from "components/WaitingView/ConfrontaValidationComponent";
import { Actions } from "containers/TransferSubscriptions/store/Actions";
import { get, isNil } from "lodash";
import { ThunkAction, ThunkDispatch } from "redux-thunk";
import {
  CajitaStore,
  IInputProps,
  IRadioGroupProps,
  ISelectItem,
  ISelectProps,
} from "root/store/CajitaStore";
import { updateDisableButton } from "root/store/Reducer";
import { ITransferSubscriptionFormProps } from "types/cajita_store";
import { TokenResponse } from "types/remote/token_response";
import { ITransferActionCreator } from "types/transfer_action_creator";
import { ITransferSubscriptionActionCreator } from "types/transfer_subscription_action_creator";
import { TransferSubscriptionTokenRequest } from "types/transfer_subscription_token_request";

function getStatusValidity(state: ITransferSubscriptionFormProps): boolean {
  let isValid: boolean = true;
  const inputs: string[] = [
    "name",
    "lastName",
    "documentNumber",
    "accountNumber",
    "email",
  ];

  for (const inputName of inputs) isValid = state[inputName].valid && isValid;

  return isValid;
}

const inputAction: (
  payload: IInputProps
) => ITransferSubscriptionActionCreator = (
  payload: IInputProps
): ITransferSubscriptionActionCreator => ({
  type: Actions.UPDATE_TRANSFER_SUBSCRIPTION_INPUT,
  input: payload,
});

const tokenAction: (
  payload: TokenResponse
) => ITransferSubscriptionActionCreator = (
  payload: TokenResponse
): ITransferSubscriptionActionCreator => ({
  type: Actions.SET_TRANSFER_SUBSCRIPTION_TOKEN,
  token: payload.token,
  secureService: payload.secureService,
  secureServiceId: payload.secureId,
});

const selectAction: (
  payload: ISelectProps
) => ITransferSubscriptionActionCreator = (
  payload: ISelectProps
): ITransferSubscriptionActionCreator => ({
  type: Actions.UPDATE_SELECT_VALUE,
  select: payload,
});

const setConfrontaQuestions: (
  payload: SecureOtpResponse
) => ITransferSubscriptionActionCreator = (
  payload: SecureOtpResponse
): ITransferSubscriptionActionCreator => ({
  type: Actions.SET_CONFRONTA_QUESTIONS,
  question: get(payload, "questions"),
  questionnaireCode: get(payload, "questionnaireCode"),
  message: get(payload, "code"),
});

const setSecureValidationError: (
  payload: ErrorResponse
) => ITransferSubscriptionActionCreator = (
  payload: ErrorResponse
): ITransferSubscriptionActionCreator => ({
  type: Actions.SET_CONFRONTA_ERROR,
  secureValidationError: get(payload, "message"),
});

const setTransferSubscriptionColombia: () => ITransferActionCreator = (): ITransferActionCreator => ({
  type: Actions.SET_TRANSFER_SUBSCRIPTION_COLOMBIA,
});

const changeStatus: () => ITransferSubscriptionActionCreator = (): ITransferSubscriptionActionCreator => ({
  type: Actions.SWITCH_TRANSFER_SUBSCRIPTION_VALIDITY,
});

const waitResponse: () => ITransferSubscriptionActionCreator = (): ITransferSubscriptionActionCreator => ({
  type: Actions.WAIT_RESPONSE,
  waitResponse: true,
});

const notWaitResponse: () => ITransferSubscriptionActionCreator = (): ITransferSubscriptionActionCreator => ({
  type: Actions.NOT_WAIT_RESPONSE,
  waitResponse: false,
});

const disableTransferSubscriptionAction: () => ITransferActionCreator = (): ITransferActionCreator => ({
  type: Actions.DISABLE_TRANSFER_SUBSCRIPTION_FORM,
});

const hideError: () => ITransferActionCreator = (): ITransferActionCreator => ({
  type: Actions.DISABLE_SECURE_VALIDATION_ERROR,
});

const updateRadioGroup: (
  payload: IRadioGroupProps
) => ITransferSubscriptionActionCreator = (
  payload: IRadioGroupProps
): ITransferSubscriptionActionCreator => ({
  type: Actions.UPDATE_RADIO_GROUP,
  radioGroup: payload,
});

const setDirty: (payload: IInputProps) => ITransferSubscriptionActionCreator = (
  payload: IInputProps
): ITransferSubscriptionActionCreator => ({
  type: Actions.SET_TRANSFER_SUBSCRIPTION_INPUT_DIRTY,
  input: payload,
});

const setSelectItemsAction: (
  payload: {
    id: string;
    items: ISelectItem[];
  }
) => ITransferSubscriptionActionCreator = (payload: {
  id: string;
  items: ISelectItem[];
}): ITransferSubscriptionActionCreator => ({
  type: Actions.SET_BANK_LIST_ITEMS,
  selectItems: payload,
});

function updateInput(
  payload: IInputProps
): ThunkAction<
  void,
  CajitaStore,
  undefined,
  ITransferSubscriptionActionCreator
> {
  return (
    dispatch: ThunkDispatch<
      CajitaStore,
      undefined,
      ITransferSubscriptionActionCreator
    >,
    getState: () => CajitaStore
  ): void => {
    dispatch(inputAction(payload));
    const state: ITransferSubscriptionFormProps = getState()
      .transferSubscriptions;
    const status: boolean = getStatusValidity(state);

    if (state.isValid !== status) dispatch(changeStatus());
  };
}

function updateSelectValue(
  payload: ISelectProps
): ThunkAction<
  void,
  CajitaStore,
  undefined,
  ITransferSubscriptionActionCreator
> {
  return (
    dispatch: ThunkDispatch<
      CajitaStore,
      undefined,
      ITransferSubscriptionActionCreator
    >,
    getState: () => CajitaStore
  ): void => {
    dispatch(selectAction(payload));
    const state: ITransferSubscriptionFormProps = getState()
      .transferSubscriptions;
    const status: boolean = getStatusValidity(state);

    if (state.isValid !== status) dispatch(changeStatus());
  };
}

function getTransferSubscriptionToken(
  body: TransferSubscriptionTokenRequest
): ThunkAction<
  void,
  CajitaStore,
  undefined,
  ITransferSubscriptionActionCreator
> {
  return (dispatch: Function, getState: () => CajitaStore): void => {
    const kushki: Kushki = getState().config.kushkiJS as Kushki;

    dispatch(updateDisableButton(true));
    dispatch(showOverlay());
    kushki.requestTransferSubscriptionToken(
      body,
      (response: TokenResponse | ErrorResponse) => {
        if ("token" in response) dispatch(tokenAction(response));
        else {
          dispatch(setErrorMessage(response.message));
          dispatch(hideOverlay());
        }
        dispatch(updateDisableButton(false));
      }
    );
  };
}

function setSelectItems(
  id: string,
  items: ISelectItem[]
): ThunkAction<
  void,
  CajitaStore,
  undefined,
  ITransferSubscriptionActionCreator
> {
  return (
    dispatch: ThunkDispatch<
      CajitaStore,
      undefined,
      ITransferSubscriptionActionCreator
    >
  ): void => {
    dispatch(
      setSelectItemsAction({
        id,
        items,
      })
    );
  };
}

function getBankList(
  id: string
): ThunkAction<
  void,
  CajitaStore,
  undefined,
  ITransferSubscriptionActionCreator
> {
  return (
    dispatch: ThunkDispatch<
      CajitaStore,
      undefined,
      ITransferSubscriptionActionCreator
    >,
    getState: () => CajitaStore
  ): void => {
    const kushki: Kushki = getState().config.kushkiJS as Kushki;

    kushki.requestTransferSubscriptionBankList(
      (response: BankListResponse | ErrorResponse) => {
        dispatch(setTransferSubscriptionColombia());

        if (Array.isArray(response)) {
          response.unshift({
            code: "0",
            name: "Elige tu banco",
          });

          return dispatch(
            setSelectItemsAction({
              id,
              items: response.map((bank: { code: string; name: string }) => ({
                value: bank.code,
                text: bank.name,
              })),
            })
          );
        }
        // tslint:disable-next-line:no-unnecessary-else
        else if (
          getState().transferSubscriptions.isTransferSubscriptionAvailable
        )
          dispatch(disableTransferSubscriptionAction());

        return;
      }
    );
  };
}

// istanbul ignore next
function hideErrorModal(): ThunkAction<
  void,
  CajitaStore,
  undefined,
  ITransferSubscriptionActionCreator
> {
  return (dispatch: Function, _getSate: Function): void => {
    dispatch(hideError());
  };
}

function getSecureValidation(
  secureServiceId: string,
  secureService: string,
  cityCode?: string,
  stateCode?: string,
  expeditionDate?: string,
  answers?: IConfrontaAnswer[],
  questionnaireCode?: string
): ThunkAction<
  void,
  CajitaStore,
  undefined,
  ITransferSubscriptionActionCreator
> {
  return (
    dispatch: ThunkDispatch<
      CajitaStore,
      undefined,
      ITransferSubscriptionActionCreator
    >,
    getState: () => CajitaStore
  ): void => {
    const kushki: Kushki = getState().config.kushkiJS as Kushki;
    let secureValidationBody: object;

    if (isNil(answers))
      secureValidationBody = {
        secureService,
        secureServiceId,
        confrontaInfo: {
          confrontaBiometrics: {
            cityCode,
            stateCode,
            expeditionDate,
          },
        },
      };
    else
      secureValidationBody = {
        secureService,
        secureServiceId,
        confrontaInfo: {
          answers,
          questionnaireCode,
        },
      };
    dispatch(waitResponse());
    kushki.requestSecureServiceValidation(
      secureValidationBody as SecureOtpRequest,
      (response: SecureOtpResponse | ErrorResponse) => {
        if (
          !isNil(get(response, "error")) ||
          get(response, "code") === "BIO100"
        ) {
          dispatch(setSecureValidationError(response as ErrorResponse));
          dispatch(notWaitResponse());
        } else {
          dispatch(setConfrontaQuestions(response));
          dispatch(notWaitResponse());
        }
      }
    );
  };
}

export {
  setDirty,
  setSelectItems,
  updateInput,
  updateSelectValue,
  updateRadioGroup,
  getBankList,
  changeStatus,
  disableTransferSubscriptionAction,
  getTransferSubscriptionToken,
  getSecureValidation,
  hideErrorModal,
};



// WEBPACK FOOTER //
// ./src/containers/TransferSubscriptions/store/ActionCreators.tsx